import { ColorModule } from '@epihub/web/src/color';
import { type AvailableTheme } from '@krishanmadan/core/src/model/const';
import { buildThemeV0, initializeTheme } from '@epihub/web/src/theme';
import { type ThemeDefV0 } from '@epihub/web/src/theme';
import { assets } from '../assets';

const IMAGE_BACKGROUND_OPACITY = 0.6;

function generateStartTheme() {
  const Palette = {
    background1: '#000000',
    background2: '#000000',
    primaryText: '#FFFFFF',
    secondaryText: 'rgba(255, 255, 255, 0.8)',
    accent1: '#DE541E',
    accent1Light: '#DE541E',
    accent1Lighter: '#DE541E',
    accent2: '#63CCCA',
    positive: '#87A330',
    chatHeadLeft: 'rgba(0, 0, 0, 0.7)',
    chatHeadRight: ColorModule.hexToRGBString('#87A330', 0.6),
  };
  return {
    id: 'start',
    colors: Palette,
    backgroundImage: assets.BackgroundDefault.url,
    background: `linear-gradient(120deg, ${
      ColorModule.hexToRGBString(Palette.background1, IMAGE_BACKGROUND_OPACITY)
    }, ${ColorModule.hexToRGBString(Palette.background2, IMAGE_BACKGROUND_OPACITY)})`,
  };
}

export const LEGACY_THEMES = (function(): Record<AvailableTheme, ReturnType<typeof generateStartTheme>> {
  return {
    start: generateStartTheme(),

    trainer: (() => {
      const Palette = {
        background1: '#000000',
        background2: '#000000',
        primaryText: '#FFFFFF',
        secondaryText: 'rgba(255, 255, 255, 0.8)',
        accent1: '#8B2635',
        accent1Light: '#FF6B6C',
        accent1Lighter: '#FE5F55',
        accent2: '#16BAC5',
        positive: '#69B578',
        chatHeadLeft: 'rgba(0, 0, 0, 0.6)',
        chatHeadRight: ColorModule.hexToRGBString('#69B578', 0.6),
      };
      return {
        id: 'trainer',
        colors: Palette,
        backgroundImage: assets.BackgroundTrainer.url,
        background: `linear-gradient(120deg, ${
          ColorModule.hexToRGBString(Palette.background1, IMAGE_BACKGROUND_OPACITY)
        }, ${ColorModule.hexToRGBString(Palette.background2, IMAGE_BACKGROUND_OPACITY)})`,
      };
    })(),

    alacarte: (() => {
      const Palette = {
        background1: '#000000',
        background2: '#000000',
        primaryText: '#FFFFFF',
        secondaryText: 'rgba(255, 255, 255, 0.8)',
        accent1: '#F15B40',
        accent1Light: '#2B2C33',
        accent1Lighter: '#2B2C33',
        accent2: '#EFB339',
        positive: '#FFDECD',
        chatHeadLeft: 'rgba(200, 200, 200, 0.3)',
        chatHeadRight: ColorModule.hexToRGBString('#F15B40', 0.6),
      };
      return {
        id: 'alacarte',
        colors: Palette,
        backgroundImage: assets.BackgroundAlacarte.url,
        background: `linear-gradient(120deg, ${
          ColorModule.hexToRGBString(Palette.background1, IMAGE_BACKGROUND_OPACITY)
        }, ${ColorModule.hexToRGBString(Palette.background2, IMAGE_BACKGROUND_OPACITY)})`,
      };
    })(),

    work: (() => {
      const Palette = {
        background1: '#000000',
        background2: '#000000',
        primaryText: '#FFFFFF',
        secondaryText: 'rgba(255, 255, 255, 0.8)',
        accent1: '#DE541E',
        accent1Light: '#DE541E',
        accent1Lighter: '#DE541E',
        accent2: '#63CCCA',
        positive: '#87A330',
        chatHeadLeft: 'rgba(0, 0, 0, 0.6)',
        chatHeadRight: ColorModule.hexToRGBString('#87A330', 0.6),
      };
      return {
        id: 'work',
        colors: Palette,
        backgroundImage: assets.BackgroundWork.url,
        background: `linear-gradient(120deg, ${
          ColorModule.hexToRGBString(Palette.background1, IMAGE_BACKGROUND_OPACITY)
        }, ${ColorModule.hexToRGBString(Palette.background2, IMAGE_BACKGROUND_OPACITY)})`,
      };
    })(),

    projects: (() => {
      const Palette = {
        background1: '#000000',
        background2: '#000000',
        primaryText: '#FFFFFF',
        secondaryText: 'rgba(255, 255, 255, 0.6)',
        accent1: '#DE541E',
        accent1Light: '#DE541E',
        accent1Lighter: '#DE541E',
        accent2: '#63CCCA',
        positive: '#4167ab',
        chatHeadLeft: 'rgba(0, 0, 0, 0.6)',
        chatHeadRight: ColorModule.hexToRGBString('#384E77', 0.6),
      };
      return {
        id: 'projects',
        colors: Palette,
        backgroundImage: assets.BackgroundTech.url,
        background: `linear-gradient(120deg, ${
          ColorModule.hexToRGBString(Palette.background1, IMAGE_BACKGROUND_OPACITY)
        }, ${ColorModule.hexToRGBString(Palette.background2, IMAGE_BACKGROUND_OPACITY)})`,
      };
    })(),
  };
})();

const themeBuilder = buildThemeV0(
  {
    mantine: {
      colors: {
        dark: [
          '#C9C9C9', // <-- Text in Dark mode
          '#b8b8b8',
          '#828282',
          '#696969',
          '#424242', // <-- Border
          '#3b3b3b',
          '#2e2e2e', // <-- Dark
          '#242424', // <-- Dark mode background
          '#1f1f1f', // <-- Primary
          '#141414',
        ],
        gray: [
          '#f8f9fa',
          '#f1f3f5',
          '#e9ecef',
          '#dee2e6',
          '#ced4da',
          '#adb5bd',
          '#868e96',
          '#495057',
          '#343a40',
          '#212529',
        ],
        red: [
          '#fff5f5',
          '#ffe3e3',
          '#ffc9c9',
          '#ffa8a8',
          '#ff8787',
          '#ff6b6b',
          '#fa5252', // <-- error
          '#f03e3e',
          '#e03131',
          '#c92a2a',
        ],
        pink: [
          '#fff0f6',
          '#ffdeeb',
          '#fcc2d7',
          '#faa2c1',
          '#f783ac',
          '#f06595',
          '#e64980',
          '#d6336c',
          '#c2255c',
          '#a61e4d',
        ],
        grape: [
          '#f8f0fc',
          '#f3d9fa',
          '#eebefa',
          '#e599f7',
          '#da77f2',
          '#cc5de8',
          '#be4bdb',
          '#ae3ec9',
          '#9c36b5',
          '#862e9c',
        ],
        violet: [
          '#f3f0ff',
          '#e5dbff',
          '#d0bfff',
          '#b197fc',
          '#9775fa',
          '#845ef7',
          '#7950f2',
          '#7048e8',
          '#6741d9',
          '#5f3dc4',
        ],
        indigo: [
          '#edf2ff',
          '#dbe4ff',
          '#bac8ff',
          '#91a7ff',
          '#748ffc',
          '#5c7cfa',
          '#4c6ef5',
          '#4263eb',
          '#3b5bdb',
          '#364fc7',
        ],
        blue: [
          '#e7f5ff',
          '#d0ebff',
          '#a5d8ff',
          '#74c0fc',
          '#4dabf7',
          '#339af0',
          '#228be6', // <-- Theme, Info
          '#1c7ed6',
          '#1971c2',
          '#1864ab',
        ],
        cyan: [
          '#e3fafc',
          '#c5f6fa',
          '#99e9f2',
          '#66d9e8',
          '#3bc9db',
          '#22b8cf',
          '#15aabf',
          '#1098ad',
          '#0c8599',
          '#0b7285',
        ],
        teal: [
          '#e6fcf5',
          '#c3fae8',
          '#96f2d7',
          '#63e6be',
          '#38d9a9',
          '#20c997',
          '#12b886',
          '#0ca678',
          '#099268',
          '#087f5b',
        ],
        green: [
          '#ebfbee',
          '#d3f9d8',
          '#b2f2bb',
          '#8ce99a',
          '#69db7c',
          '#51cf66',
          '#40c057', // <-- success
          '#37b24d',
          '#2f9e44',
          '#2b8a3e',
        ],
        lime: [
          '#f4fce3',
          '#e9fac8',
          '#d8f5a2',
          '#c0eb75',
          '#a9e34b',
          '#94d82d',
          '#82c91e',
          '#74b816',
          '#66a80f',
          '#5c940d',
        ],
        yellow: [
          '#fff9db',
          '#fff3bf',
          '#ffec99',
          '#ffe066',
          '#ffd43b',
          '#fcc419',
          '#fab005', // <-- warning
          '#f59f00',
          '#f08c00',
          '#e67700',
        ],
        orange: [
          '#fff4e6',
          '#ffe8cc',
          '#ffd8a8',
          '#ffc078',
          '#ffa94d',
          '#ff922b',
          '#fd7e14', // <-- Accent
          '#f76707',
          '#e8590c',
          '#d9480f',
        ],
      },
      primaryColor: 'blue',
      primaryShade: {
        light: 6,
        dark: 8,
      },
      white: '#ffffff',
      black: '#000000', // <-- Text in Light Mode
      // isThemeDependentPrimaryShade: true,
      defaultGradient: {
        from: 'blue',
        to: 'cyan',
        deg: 45,
      },
      fontFamily: 'Roboto',
      fontFamilyMonospace: 'Roboto Mono',
      headings: {
        fontFamily: 'Roboto',
        fontWeight: '700',
        sizes: {
          h1: {
            fontSize: '2.125rem',
            lineHeight: '1.3',
            fontWeight: '700',
          },
          h2: {
            fontSize: '1.625rem',
            lineHeight: '1.35',
            fontWeight: '700',
          },
          h3: {
            fontSize: '1.375rem',
            lineHeight: '1.4',
            fontWeight: '700',
          },
          h4: {
            fontSize: '1.125rem',
            lineHeight: '1.45',
            fontWeight: '700',
          },
          h5: {
            fontSize: '1rem',
            lineHeight: '1.5',
            fontWeight: '700',
          },
          h6: {
            fontSize: '0.875rem',
            lineHeight: '1.5',
            fontWeight: '700',
          },
        },
      },
      scale: 1,
      radius: {
        xs: '0.125rem',
        sm: '0.25rem',
        md: '0.5rem',
        lg: '1rem',
        xl: '2rem',
      },
      spacing: {
        xs: '0.625rem',
        sm: '0.75rem',
        md: '1rem',
        lg: '1.25rem',
        xl: '2rem',
      },
      defaultRadius: 'md',
      breakpoints: {
        xs: '36em',
        sm: '48em',
        md: '62em',
        lg: '75em',
        xl: '88em',
      },
      fontSmoothing: true,
      focusRing: 'auto',

      components: {
        Button: {
          defaultProps: {
            overrideTransitionProps: null,
            // animationOnPress: 'ripple',
            // rippleOpacity: 0.2,
          },
        },
        ActionIcon: {
          defaultProps: {
            overrideTransitionProps: null,
            // animationOnPress: 'ripple',
            // rippleOpacity: 0.2,
          },
        },
      },
    },
  },
  (
    creator,
    state: ThemeDefV0['State'] & {
      legacy: typeof LEGACY_THEMES[keyof typeof LEGACY_THEMES];
    },
  ) => {
    return {
      // Apply any overrides you want to the creator + state combo
      // this is recalculated each time state changes
      // creator, on the other hand, should never change
      // The equation is always (Creator + State = Theme)
      legacy: state.legacy,
    };
  },
);

export const theme = initializeTheme({
  builder: themeBuilder,
  initialState: {
    __version: 'v0',
    scheme: 'dark',
    legacy: LEGACY_THEMES.start,
  },
});

// app
export const ThemeProvider = theme.Provider;

// emotion
export const styled = theme.emotion.styled;
export const useTheme = theme.emotion.useTheme;
export const css = theme.emotion.css;

// zustand
export const zustandThemeSlice = theme.zustand;

// snackbars
export const snackbars = theme.snackbars;
